import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";
import { useConfirm } from "../../components/ConfirmDialog/ConfirmDialog";
import { getCurrentUser } from "../../services/auth_service";
import {
    isSuperadmin,
    hasPermission,
} from "../utils/permissions";
import {
    getSuppliers,
    getSupplierById,
    getSupplierTypes,
    getServiceLocations,
    getBranches,
    getStatuses,
    getFinancialYears,
    deleteSupplier,
} from "../../services/supplier_service";

import "./Suppliers.css";

import {
    FiSearch, FiPlus, FiEdit2, FiTrash2, FiX,
    FiFilter, FiAlertCircle, FiRefreshCw,
    FiUsers,
    FiChevronLeft, FiChevronRight,
    FiUser, FiFileText,
    FiCreditCard, FiDroplet, FiZap, FiEye, FiDownload,
    FiPrinter, FiChevronDown
} from "react-icons/fi";

const DOC_HOST = "https://srinivasafleet.com/api";
const resolveDocUrl = (url) => {
    if (!url) return url;
    if (url.startsWith(DOC_HOST)) return url;
    if (/^https?:\/\//i.test(url)) {
        try {
            const path = new URL(url).pathname;
            return `${DOC_HOST}${path}`;
        } catch {
            return url;
        }
    }
    return `${DOC_HOST}${url.startsWith("/") ? "" : "/"}${url}`;
};

const formatServerDateTime = (value) => {
    if (!value) return "-";
    const hasOffset = /Z$|[+-]\d{2}:?\d{2}$/.test(value);
    const iso = hasOffset ? value : `${value}Z`;
    const date = new Date(iso);
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleString("en-GB", { timeZone: "Asia/Kolkata" });
};

const OUTSTANDING_RANGE_OPTIONS = [
    { value: "", label: "All Outstanding" },
    { value: "no_dues", label: "No Dues" },
    { value: "0", label: "₹0 – ₹49,999" },
    { value: "50000", label: "₹50,000 – ₹99,999" },
    { value: "100000", label: "₹1L – ₹1.99L" },
    { value: "200000", label: "₹2L – ₹2.99L" },
    { value: "300000", label: "₹3L – ₹3.99L" },
    { value: "400000", label: "₹4L – ₹4.99L" },
    { value: "500000", label: "₹5L – ₹5.99L" },
    { value: "600000", label: "₹6L – ₹6.99L" },
    { value: "700000", label: "₹7L – ₹7.99L" },
    { value: "800000", label: "₹8L – ₹8.99L" },
    { value: "900000", label: "₹9L – ₹9.99L" },
    { value: "1000000", label: "₹10L & above" },
];

const OUTSTANDING_BOUNDS = OUTSTANDING_RANGE_OPTIONS
    .map((o) => o.value)
    .filter((v) => v !== "" && v !== "no_dues")
    .map(Number);
const RECORD_STATUS_OPTIONS = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
];
const PAGE_SIZE = 10;

const TYPE_OWN_FLEET = 1;
const TYPE_DCO = 2;
const TYPE_VDP = 3;

const DOC_FIELDS = [
    { key: "adhaar_url", label: "Aadhaar Card", icon: FiFileText },
    { key: "license_file_url", label: "Driving License", icon: FiFileText },
    { key: "pancard_file_url", label: "Pancard", icon: FiCreditCard },
    { key: "bank_passbook_photo_url", label: "Bank Passbook", icon: FiFileText },
    { key: "gas_bill_photo_url", label: "Gas Bill", icon: FiDroplet },
    { key: "electricity_bill_photo_url", label: "Electricity Bill", icon: FiZap },
    { key: "photo_url", label: "Driver Photo", icon: FiUser },
    { key: "payment_receipt_url", label: "Payment Receipt", icon: FiCreditCard },
    { key: "agreement_document_url", label: "Agreement Document", icon: FiFileText },
];

const isImageUrl = (url) => !!url && /\.(jpg|jpeg|png|webp|gif)$/i.test(url);
const VIEW_KNOWN_KEYS = new Set([
    "id", "supplier_code", "supplier_name", "mobile", "alternate_mobile",
    "email", "aadhaar_number", "city", "state", "pincode",
    "branch_id", "service_location_id", "supplier_type_id", "status_id",
    "total_outstanding", "remarks", "created_at", "updated_at",
    "financial_year_id", "max_vehicle_capacity", "is_active",
    "created_by", "updated_by", "created_by_name", "updated_by_name",
    ...DOC_FIELDS.map((f) => f.key),
]);

const humanizeKey = (key) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const ACTION_MENU_MARGIN = 8;

export default function Suppliers() {
    const navigate = useNavigate();
    const currentUser = getCurrentUser() || {};
    const confirm = useConfirm();

    const isSuperadminUser = isSuperadmin();
    const canViewSuppliers =
        isSuperadminUser || hasPermission("suppliers", "view");
    const canEditSuppliers =
        isSuperadminUser || hasPermission("suppliers", "edit");
    const canDeleteSuppliers =
        isSuperadminUser || hasPermission("suppliers", "delete");

    const hasSupplierAccess =
        isSuperadminUser ||
        canViewSuppliers ||
        canEditSuppliers ||
        canDeleteSuppliers;
    const [suppliers, setSuppliers] = useState([]);
    const [supplierTypes, setSupplierTypes] = useState([]);
    const [locations, setLocations] = useState([]);
    const [branches, setBranches] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [financialYears, setFinancialYears] = useState([]);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [filterPanelOpen, setFilterPanelOpen] = useState(false);
    const filterPanelRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");
    const [deletingId, setDeletingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedSupplierType, setSelectedSupplierType] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("");
    const [selectedLocation, setSelectedLocation] = useState("");
    const [selectedBranch, setSelectedBranch] = useState("");
    const [selectedOutstandingRange, setSelectedOutstandingRange] = useState("");
    const [selectedFinancialYear, setSelectedFinancialYear] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [recordStatus, setRecordStatus] = useState("active");

    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewLabel, setPreviewLabel] = useState("");

    const [docModalSupplier, setDocModalSupplier] = useState(null);

    const [viewSupplier, setViewSupplier] = useState(null);
    const [viewLoading, setViewLoading] = useState(false);
    const [viewError, setViewError] = useState("");
    const [pdfGenerating, setPdfGenerating] = useState(false);
    const [printing, setPrinting] = useState(false);

    const [openActionRowId, setOpenActionRowId] = useState(null);
    const [openActionIsInactive, setOpenActionIsInactive] = useState(false);
    const [menuPos, setMenuPos] = useState(null);
    const actionMenuRef = useRef(null);
    const actionButtonRefs = useRef({});

    useEffect(() => {
        if (!hasSupplierAccess) {
            setLoading(false);
            return;
        }
        loadData();
    }, []);

    useEffect(() => {
        if (!hasSupplierAccess) return;
        loadSuppliers(selectedFinancialYear).catch(() => setErrorMsg("Couldn't load suppliers."));
    }, [selectedFinancialYear, recordStatus]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (filterPanelRef.current && !filterPanelRef.current.contains(e.target)) {
                setFilterPanelOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const closeActionMenu = () => {
        setOpenActionRowId(null);
        setOpenActionIsInactive(false);
        setMenuPos(null);
    };

    useEffect(() => {
        if (openActionRowId === null) return;
        const handleClickOutside = (e) => {
            const btn = actionButtonRefs.current[openActionRowId];
            if (
                actionMenuRef.current && !actionMenuRef.current.contains(e.target) &&
                btn && !btn.contains(e.target)
            ) {
                closeActionMenu();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [openActionRowId]);

    useEffect(() => {
        if (openActionRowId === null) return;
        const handleReposition = () => closeActionMenu();
        const wrap = document.querySelector(".sup-table-wrap");
        wrap?.addEventListener("scroll", handleReposition, { passive: true });
        window.addEventListener("resize", handleReposition);
        return () => {
            wrap?.removeEventListener("scroll", handleReposition);
            window.removeEventListener("resize", handleReposition);
        };
    }, [openActionRowId]);

    const toggleActionMenu = (e, supplierId, isInactiveRecord) => {
        e.stopPropagation();
        if (openActionRowId === supplierId) {
            closeActionMenu();
            return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        const left = Math.min(
            rect.right,
            window.innerWidth - ACTION_MENU_MARGIN
        );
        const top = Math.min(
            rect.bottom + 6,
            window.innerHeight - ACTION_MENU_MARGIN
        );
        setMenuPos({ top, left });
        setOpenActionIsInactive(isInactiveRecord);
        setOpenActionRowId(supplierId);
    };

    const handleFinancialYearChange = (value) => {
        setSelectedFinancialYear(value);
        setDateFrom("");
        setDateTo("");
    };

    const selectedFYObj = useMemo(
        () => financialYears.find((fy) => String(fy.id) === String(selectedFinancialYear)),
        [financialYears, selectedFinancialYear]
    );
    const showFYDateRange = !!selectedFinancialYear && selectedFinancialYear !== "all";

    const loadData = async () => {
        setLoading(true);
        setErrorMsg("");
        try {
            const [suppliersData, supplierTypesData, locationsData, branchesData, statusesData, financialYearsData] =
                await Promise.all([
                    getSuppliers({ status: recordStatus }), getSupplierTypes(), getServiceLocations(),
                    getBranches(), getStatuses(), getFinancialYears(),
                ]);
            setSuppliers(suppliersData || []);
            setSupplierTypes(supplierTypesData || []);
            setLocations(locationsData || []);
            setBranches(branchesData || []);
            setStatuses(statusesData || []);
            setFinancialYears(financialYearsData || []);
        } catch {
            setErrorMsg("Couldn't load suppliers. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const loadSuppliers = async (fyValue) => {
        const opts =
            fyValue === "all" ? { allYears: true, status: recordStatus }
                : fyValue ? { financialYearId: fyValue, status: recordStatus }
                    : { status: recordStatus };
        const data = await getSuppliers(opts);
        setSuppliers(data || []);
    };

    const getSupplierTypeName = (id) =>
        supplierTypes.find((t) => Number(t.id) === Number(id))?.supplier_type_name || "-";

    const getStatusName = (id) => {
        const s = statuses.find((s) => Number(s.id) === Number(id));
        return s?.name || s?.status_name || "-";
    };

    const getLocationName = (id) => {
        const l = locations.find((l) => Number(l.id) === Number(id));
        return l?.location_name || l?.name || "-";
    };

    const getBranchName = (id) => {
        const b = branches.find((b) => Number(b.id) === Number(id));
        return b?.branch_name || "-";
    };

    const getFinancialYearLabel = (id) =>
        financialYears.find((fy) => Number(fy.id) === Number(id))?.fy_label || "-";

    const hasOutstandingField = (s) =>
        Number(s.supplier_type_id) === TYPE_DCO || Number(s.supplier_type_id) === TYPE_VDP;

    const matchesOutstandingRange = (s) => {
        if (!selectedOutstandingRange) return true;

        const dues = Number(s.total_outstanding || 0);

        if (selectedOutstandingRange === "no_dues") {
            return !hasOutstandingField(s) || dues <= 0;
        }

        if (!hasOutstandingField(s)) return false;

        const lower = Number(selectedOutstandingRange);
        const idx = OUTSTANDING_BOUNDS.indexOf(lower);
        const upper = idx >= 0 && idx < OUTSTANDING_BOUNDS.length - 1
            ? OUTSTANDING_BOUNDS[idx + 1]
            : Infinity;

        return dues >= lower && dues < upper;
    };

    const matchesDateRange = (s) => {
        if (!dateFrom && !dateTo) return true;
        if (!s.created_at) return false;

        const created = new Date(s.created_at);
        if (dateFrom && created < new Date(dateFrom)) return false;
        if (dateTo) {
            const to = new Date(dateTo);
            to.setHours(23, 59, 59, 999);
            if (created > to) return false;
        }
        return true;
    };
    const resetFilters = () => {
        setSearchTerm(""); setSelectedSupplierType("");
        setSelectedStatus(""); setSelectedLocation(""); setSelectedBranch("");
        setSelectedOutstandingRange("");
        setSelectedFinancialYear("");
        setDateFrom(""); setDateTo("");
        setRecordStatus("active");
        setFilterPanelOpen(false);
    };

    const filteredSuppliers = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        return suppliers.filter((s) => {
            const matchesSearch =
                !term ||
                s.supplier_name?.toLowerCase().includes(term) ||
                s.supplier_id?.toLowerCase().includes(term) ||
                s.supplier_code?.toLowerCase().includes(term) ||
                s.mobile?.toLowerCase().includes(term) ||
                s.city?.toLowerCase().includes(term) ||
                s.remarks?.toLowerCase().includes(term);
            const matchesType = !selectedSupplierType || Number(s.supplier_type_id) === Number(selectedSupplierType);
            const matchesStatus = !selectedStatus || Number(s.status_id) === Number(selectedStatus);
            const matchesLocation = !selectedLocation || Number(s.service_location_id) === Number(selectedLocation);
            const matchesBranch = !selectedBranch || Number(s.branch_id) === Number(selectedBranch);
            const matchesFY = !selectedFinancialYear || Number(s.financial_year_id) === Number(selectedFinancialYear);
            return matchesSearch && matchesType && matchesStatus && matchesLocation && matchesBranch && matchesFY && matchesOutstandingRange(s) && matchesDateRange(s);
        });
    }, [suppliers, searchTerm, selectedSupplierType, selectedStatus, selectedLocation, selectedBranch, selectedOutstandingRange, selectedFinancialYear, dateFrom, dateTo]);

    useEffect(() => { setCurrentPage(1); },
        [searchTerm, selectedSupplierType, selectedStatus, selectedLocation, selectedBranch, selectedOutstandingRange, selectedFinancialYear, dateFrom, dateTo, recordStatus]);
    const totalPages = Math.max(1, Math.ceil(filteredSuppliers.length / PAGE_SIZE));
    const paginatedSuppliers = useMemo(() => {
        const start = (currentPage - 1) * PAGE_SIZE;
        return filteredSuppliers.slice(start, start + PAGE_SIZE);
    }, [filteredSuppliers, currentPage]);

    useEffect(() => { closeActionMenu(); }, [currentPage, paginatedSuppliers]);

    const branchesForSelectedLocation = useMemo(() => {
        if (!selectedLocation) return branches;
        return branches.filter((b) => Number(b.location_id) === Number(selectedLocation));
    }, [branches, selectedLocation]);

    useEffect(() => {
        if (!selectedBranch) return;
        const stillValid = branchesForSelectedLocation.some(
            (b) => Number(b.id) === Number(selectedBranch)
        );
        if (!stillValid) setSelectedBranch("");
    }, [selectedLocation, branchesForSelectedLocation]);

    const rangeStart = filteredSuppliers.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
    const rangeEnd = Math.min(currentPage * PAGE_SIZE, filteredSuppliers.length);

    const goToPage = (page) => setCurrentPage(Math.min(Math.max(1, page), totalPages));
    const activeFilterCount = [
        selectedSupplierType, selectedStatus, selectedLocation, selectedBranch,
        selectedOutstandingRange, selectedFinancialYear, dateFrom, dateTo,
    ].filter(Boolean).length + (recordStatus !== "active" ? 1 : 0);

    const filtersActive = activeFilterCount > 0 || !!searchTerm;

    if (!hasSupplierAccess) {
        return (
            <div className="sup-shell">
                <Sidebar />
                <div className="sup-main">
                    <Header />
                    <div className="sup-page">
                        <div className="sup-error">
                            <FiAlertCircle size={16} />
                            <span>You don't have permission to view suppliers.</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const ownFleetCount = filteredSuppliers.filter((s) => Number(s.supplier_type_id) === TYPE_OWN_FLEET).length;
    const dcoCount = filteredSuppliers.filter((s) => Number(s.supplier_type_id) === TYPE_DCO).length;
    const vdpCount = filteredSuppliers.filter((s) => Number(s.supplier_type_id) === TYPE_VDP).length;

    const activeCount = filteredSuppliers.filter((s) => {
        const name = getStatusName(s.status_id).toLowerCase();
        return name.includes("active") && !name.includes("inactive");
    }).length;

    const inactiveCount = filteredSuppliers.filter((s) =>
        getStatusName(s.status_id).toLowerCase().includes("inactive") ||
        getStatusName(s.status_id).toLowerCase().includes("deactivate") ||
        getStatusName(s.status_id).toLowerCase().includes("block")
    ).length;

    const totalDues = filteredSuppliers
        .filter(hasOutstandingField)
        .reduce((sum, s) => sum + Number(s.total_outstanding || 0), 0);

    const handleDelete = async (id) => {
        if (!canDeleteSuppliers) return;

        const ok = await confirm({
            title: "Delete Supplier",
            message: "Are you sure you want to delete this supplier? This action cannot be undone.",
            confirmLabel: "Delete",
            cancelLabel: "Cancel",
            variant: "danger",
        });
        if (!ok) return;

        setDeletingId(id);
        try {
            await deleteSupplier(id);
            await loadData();
        } catch {
            alert("Failed to delete supplier");
        } finally {
            setDeletingId(null);
        }
    };

    const statusClass = (name) => {
        const n = name.toLowerCase();
        if (n.includes("active") && !n.includes("in")) return "status-active";
        if (n.includes("inactive") || n.includes("deactivat")) return "status-inactive";
        if (n.includes("block")) return "status-inactive";
        if (n.includes("pending")) return "status-pending";
        return "status-default";
    };

    const openPreview = (url, label) => {
        if (!url) return;
        setPreviewUrl(url);
        setPreviewLabel(label);
    };

    const closePreview = () => {
        setPreviewUrl(null);
        setPreviewLabel("");
    };

    const openDocModal = (s) => setDocModalSupplier(s);
    const closeDocModal = () => setDocModalSupplier(null);

    const openViewModal = async (id, fallback) => {
        setViewSupplier(fallback || { id });
        setViewError("");
        setViewLoading(true);
        try {
            const data = await getSupplierById(id);
            setViewSupplier(data || fallback);
        } catch {
            setViewError("Couldn't load full supplier details. Showing available data.");
        } finally {
            setViewLoading(false);
        }
    };

    const closeViewModal = () => {
        setViewSupplier(null);
        setViewError("");
    };
    const loadImageAsDataUrl = async (url) => {
        if (!url) return null;
        try {
            const res = await fetch(url, { cache: "no-store" });
            if (!res.ok) return null;
            const blob = await res.blob();
            return await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result || null);
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(blob);
            });
        } catch (err) {
            console.warn("Image fetch error:", url, err);
            return null;
        }
    };
    const getImageDimensions = (dataUrl) =>
        new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
            img.onerror = () => resolve(null);
            img.src = dataUrl;
        });

    const imageFormatFromDataUrl = (dataUrl) => {
        if (dataUrl.startsWith("data:image/png")) return "PNG";
        if (dataUrl.startsWith("data:image/webp")) return "WEBP";
        return "JPEG";
    };

    const gatherDocImages = async () => {
        const docImages = {};
        await Promise.all(
            DOC_FIELDS.map(async ({ key }) => {
                const rawUrl = viewSupplier[key];
                if (!rawUrl || !isImageUrl(rawUrl)) return;
                const url = resolveDocUrl(rawUrl);
                const dataUrl = await loadImageAsDataUrl(url);
                if (!dataUrl) return;
                const dims = await getImageDimensions(dataUrl);
                if (dims) docImages[key] = { dataUrl, dims };
            })
        );
        return docImages;
    };

    const handleDownloadView = async () => {
        if (!viewSupplier) return;
        setPdfGenerating(true);

        try {
            const docImages = await gatherDocImages();
            buildAndSavePdf(docImages, "save");
        } finally {
            setPdfGenerating(false);
        }
    };

    const printPdfDoc = (doc) => {
        const blobUrl = doc.output("bloburl");

        const iframe = document.createElement("iframe");
        iframe.style.position = "fixed";
        iframe.style.right = "0";
        iframe.style.bottom = "0";
        iframe.style.width = "0";
        iframe.style.height = "0";
        iframe.style.border = "0";
        iframe.src = blobUrl;

        const cleanup = () => {
            if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        };

        iframe.onload = () => {
            try {
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
            } catch (err) {
                console.warn("Inline print failed, opening in a new tab instead:", err);
                window.open(blobUrl, "_blank");
            }
            setTimeout(cleanup, 60000);
        };

        document.body.appendChild(iframe);
    };

    const handlePrintView = async () => {
        if (!viewSupplier) return;
        setPrinting(true);

        try {
            const docImages = await gatherDocImages();
            buildAndSavePdf(docImages, "print");
        } finally {
            setPrinting(false);
        }
    };

    const buildAndSavePdf = (docImages, mode = "save") => {
        const doc = new jsPDF({ unit: "pt", format: "a4" });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const marginX = 40;
        const contentWidth = pageWidth - marginX * 2;
        const footerY = pageHeight - 28;
        let y = 0;
        let pageNum = 1;

        const BRAND = [37, 99, 235];
        const LABEL_GRAY = [130, 130, 130];
        const VALUE_DARK = [30, 30, 30];
        const SECTION_GRAY = [90, 90, 90];
        const CARD_BG = [248, 249, 251];
        const CARD_BORDER = [230, 232, 236];

        const drawFooter = () => {
            doc.setFont(undefined, "normal");
            doc.setFontSize(8);
            doc.setTextColor(160, 160, 160);
            doc.text(`Page ${pageNum}`, pageWidth - marginX, footerY, { align: "right" });
            doc.text(
                `Generated ${new Date().toLocaleDateString("en-IN")}`,
                marginX, footerY
            );
        };

        const addPage = () => {
            drawFooter();
            doc.addPage();
            pageNum += 1;
            y = 40;
        };

        const ensureSpace = (needed) => {
            if (y + needed > footerY - 10) addPage();
        };

        const addSectionTitle = (title) => {
            ensureSpace(26);
            doc.setFillColor(...BRAND);
            doc.rect(marginX, y, 3, 12, "F");
            doc.setFont(undefined, "bold");
            doc.setFontSize(10.5);
            doc.setTextColor(...SECTION_GRAY);
            doc.text(title.toUpperCase(), marginX + 10, y + 10);
            y += 22;
        };

        const addFieldGrid = (fields, gridWidth = contentWidth) => {
            const colGap = 20;
            const colWidth = (gridWidth - colGap) / 2;
            const rowPadY = 10;
            const labelSize = 8.2;
            const valueSize = 10;
            const lineH = 12.5;

            const rows = [];
            for (let i = 0; i < fields.length; i += 2) {
                rows.push([fields[i], fields[i + 1] || null]);
            }

            rows.forEach((row) => {
                doc.setFont(undefined, "normal");
                doc.setFontSize(valueSize);
                const wrappedCells = row.map((f) => {
                    if (!f) return null;
                    const text = f[1] === undefined || f[1] === null || f[1] === "" ? "-" : String(f[1]);
                    return doc.splitTextToSize(text, colWidth - 4);
                });
                const rowLines = Math.max(...wrappedCells.map((w) => (w ? w.length : 1)));
                const rowHeight = labelSize + 4 + rowLines * lineH + rowPadY;

                ensureSpace(rowHeight);

                row.forEach((f, idx) => {
                    if (!f) return;
                    const cellX = marginX + idx * (colWidth + colGap);
                    let cy = y;

                    doc.setFont(undefined, "normal");
                    doc.setFontSize(labelSize);
                    doc.setTextColor(...LABEL_GRAY);
                    doc.text(f[0].toUpperCase(), cellX, cy);
                    cy += labelSize + 5;

                    doc.setFont(undefined, "bold");
                    doc.setFontSize(valueSize);
                    doc.setTextColor(...VALUE_DARK);
                    doc.text(wrappedCells[idx], cellX, cy);
                });

                y += rowHeight;
                doc.setDrawColor(...CARD_BORDER);
                doc.line(marginX, y - rowPadY / 2, marginX + gridWidth, y - rowPadY / 2);
            });

            y += 6;
        };

        const addDocumentGrid = () => {
            const cols = 3;
            const gap = 14;
            const cellW = (contentWidth - gap * (cols - 1)) / cols;
            const cellH = 130;
            const labelH = 16;

            let col = 0;
            let rowTopY = 0;

            const startRow = () => {
                ensureSpace(labelH + cellH + 22);
                rowTopY = y;
                col = 0;
            };
            startRow();

            DOC_FIELDS.forEach(({ key, label }) => {
                if (col === cols) {
                    y = rowTopY + labelH + cellH + 22;
                    startRow();
                }

                const cellX = marginX + col * (cellW + gap);

                doc.setFont(undefined, "normal");
                doc.setFontSize(8.5);
                doc.setTextColor(...LABEL_GRAY);
                doc.text(label, cellX, rowTopY + 9);

                const boxY = rowTopY + labelH;
                doc.setDrawColor(...CARD_BORDER);
                doc.setFillColor(...CARD_BG);
                doc.roundedRect(cellX, boxY, cellW, cellH, 4, 4, "FD");

                const url = viewSupplier[key];
                const imgData = docImages[key];

                if (url && imgData) {
                    const { dataUrl, dims } = imgData;
                    const pad = 6;
                    const maxW = cellW - pad * 2;
                    const maxH = cellH - pad * 2;
                    let w = dims.width, h = dims.height;
                    const scale = Math.min(maxW / w, maxH / h, 1);
                    w *= scale; h *= scale;
                    const imgX = cellX + (cellW - w) / 2;
                    const imgY = boxY + (cellH - h) / 2;
                    const format = imageFormatFromDataUrl(dataUrl);
                    try {
                        doc.addImage(dataUrl, format, imgX, imgY, w, h);
                    } catch {
                        doc.setFont(undefined, "italic");
                        doc.setFontSize(8);
                        doc.setTextColor(...LABEL_GRAY);
                        doc.text("Preview unavailable", cellX + 8, boxY + cellH / 2);
                    }
                } else if (url) {
                    doc.setFont(undefined, "italic");
                    doc.setFontSize(8);
                    doc.setTextColor(...LABEL_GRAY);
                    doc.text("Uploaded — preview", cellX + 10, boxY + cellH / 2 - 5);
                    doc.text("unavailable offline", cellX + 10, boxY + cellH / 2 + 7);
                } else {
                    doc.setFont(undefined, "italic");
                    doc.setFontSize(8.5);
                    doc.setTextColor(190, 190, 190);
                    doc.text("Not uploaded", cellX + 10, boxY + cellH / 2);
                }

                col += 1;
            });

            y = rowTopY + labelH + cellH + 22;
        };

        const bannerH = 78;
        doc.setFillColor(...BRAND);
        doc.rect(0, 0, pageWidth, bannerH, "F");

        const avatarR = 22;
        const avatarCx = marginX + avatarR;
        const avatarCy = bannerH / 2;
        const photoData = docImages["photo_url"];
        if (photoData) {
            try {
                doc.saveGraphicsState?.();
                const format = imageFormatFromDataUrl(photoData.dataUrl);
                doc.addImage(
                    photoData.dataUrl, format,
                    avatarCx - avatarR, avatarCy - avatarR, avatarR * 2, avatarR * 2
                );
            } catch { }
        } else {
            doc.setFillColor(255, 255, 255);
            doc.circle(avatarCx, avatarCy, avatarR, "F");
            doc.setFont(undefined, "bold");
            doc.setFontSize(16);
            doc.setTextColor(...BRAND);
            const initial = (viewSupplier.supplier_name || "S").trim().charAt(0).toUpperCase();
            doc.text(initial, avatarCx, avatarCy + 5, { align: "center" });
        }

        const textX = marginX + avatarR * 2 + 16;
        doc.setFont(undefined, "bold");
        doc.setFontSize(17);
        doc.setTextColor(255, 255, 255);
        doc.text(viewSupplier.supplier_name || "Supplier", textX, bannerH / 2 - 6);

        doc.setFont(undefined, "normal");
        doc.setFontSize(9.5);
        doc.setTextColor(225, 231, 250);
        doc.text(
            `${viewSupplier.supplier_code || "-"}   ·   ${viewSupplier.mobile || "-"}   ·   ${getSupplierTypeName(viewSupplier.supplier_type_id)}`,
            textX, bannerH / 2 + 13
        );

        const statusText = getStatusName(viewSupplier.status_id);
        doc.setFont(undefined, "bold");
        doc.setFontSize(9);
        const pillPadX = 10;
        const pillW = doc.getTextWidth(statusText) + pillPadX * 2;
        const pillX = pageWidth - marginX - pillW;
        const pillY = bannerH / 2 - 9;
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(pillX, pillY, pillW, 18, 9, 9, "F");
        doc.setTextColor(...BRAND);
        doc.text(statusText, pillX + pillW / 2, pillY + 12.5, { align: "center" });

        y = bannerH + 26;

        addSectionTitle("Contact");

        const photoBoxW = 110;
        const photoBoxH = 130;
        const photoBoxGap = 18;
        const photoBoxX = marginX + contentWidth - photoBoxW;
        ensureSpace(photoBoxH + 10);
        const boxY = y;

        doc.setDrawColor(...CARD_BORDER);
        doc.setFillColor(...CARD_BG);
        doc.roundedRect(photoBoxX, boxY, photoBoxW, photoBoxH, 4, 4, "FD");

        const driverPhoto = docImages["photo_url"];
        if (driverPhoto) {
            const { dataUrl, dims } = driverPhoto;
            const pad = 6;
            const maxW = photoBoxW - pad * 2;
            const maxH = photoBoxH - pad * 2;
            let w = dims.width, h = dims.height;
            const scale = Math.min(maxW / w, maxH / h, 1);
            w *= scale; h *= scale;
            const imgX = photoBoxX + (photoBoxW - w) / 2;
            const imgY = boxY + (photoBoxH - h) / 2;
            try {
                const format = imageFormatFromDataUrl(dataUrl);
                doc.addImage(dataUrl, format, imgX, imgY, w, h);
            } catch { }
        } else {
            doc.setFont(undefined, "italic");
            doc.setFontSize(8.5);
            doc.setTextColor(190, 190, 190);
            doc.text("No photo", photoBoxX + photoBoxW / 2, boxY + photoBoxH / 2, { align: "center" });
        }

        doc.setFont(undefined, "normal");
        doc.setFontSize(7.5);
        doc.setTextColor(...LABEL_GRAY);
        doc.text("DRIVER PHOTO", photoBoxX + photoBoxW / 2, boxY + photoBoxH + 11, { align: "center" });

        const contactGridWidth = contentWidth - photoBoxW - photoBoxGap;
        addFieldGrid([
            ["Supplier Code", viewSupplier.supplier_code],
            ["Name", viewSupplier.supplier_name],
            ["Mobile", viewSupplier.mobile],
            ["Alt. Mobile", viewSupplier.alternate_mobile],
            ["Email", viewSupplier.email],
            ["Aadhaar", viewSupplier.aadhaar_number],
        ], contactGridWidth);

        const photoBottom = boxY + photoBoxH + 20;
        if (y < photoBottom) y = photoBottom;

        addSectionTitle("Location");
        addFieldGrid([
            ["City", viewSupplier.city],
            ["State", viewSupplier.state],
            ["Pincode", viewSupplier.pincode],
            ["Branch", getBranchName(viewSupplier.branch_id)],
            ["Service Location", getLocationName(viewSupplier.service_location_id)],
        ]);

        addSectionTitle("Status");
        addFieldGrid([
            ["Type", getSupplierTypeName(viewSupplier.supplier_type_id)],
            ["Status", getStatusName(viewSupplier.status_id)],
            [
                "Outstanding",
                hasOutstandingField(viewSupplier)
                    ? `Rs. ${Number(viewSupplier.total_outstanding || 0).toLocaleString("en-IN")}`
                    : "-",
            ],
            [
                "Max Vehicle Capacity",
                hasOutstandingField(viewSupplier)
                    ? (viewSupplier.max_vehicle_capacity ?? "-")
                    : "-",
            ],
            ["Financial Year", getFinancialYearLabel(viewSupplier.financial_year_id)],
            ["Record Status", viewSupplier.is_active === false ? "Inactive (Deleted)" : "Active"],
            ["Remarks", viewSupplier.remarks],
        ]);
        addSectionTitle("Audit");
        addFieldGrid([
            ["Created By", viewSupplier.created_by_name || viewSupplier.created_by],
            ["Updated By", viewSupplier.updated_by_name || viewSupplier.updated_by],
            ["Created At", formatServerDateTime(viewSupplier.created_at)],
            ["Updated At", formatServerDateTime(viewSupplier.updated_at)],
        ]);

        addSectionTitle("Documents");
        addDocumentGrid();

        const extraEntries = Object.entries(viewSupplier).filter(
            ([k, v]) => !VIEW_KNOWN_KEYS.has(k) && v !== null && v !== undefined && v !== ""
        );
        if (extraEntries.length > 0) {
            addSectionTitle("Additional Details");
            addFieldGrid(
                extraEntries.map(([k, v]) => [
                    humanizeKey(k),
                    typeof v === "object" ? JSON.stringify(v) : String(v),
                ])
            );
        }

        drawFooter();

        const safeName = (viewSupplier.supplier_code || viewSupplier.supplier_name || "supplier")
            .toString()
            .trim()
            .replace(/\s+/g, "_");

        if (mode === "print") {
            printPdfDoc(doc);
        } else {
            doc.save(`${safeName}.pdf`);
        }
    };

    const docCount = (s) => DOC_FIELDS.filter(({ key }) => !!s[key]).length;

    const openActionSupplier = openActionRowId !== null
        ? paginatedSuppliers.find((s) => s.id === openActionRowId)
        : null;

    return (
        <div className="sup-shell">
            <Sidebar />
            <div className="sup-main">
                <Header />
                <div className="sup-page">

                    <div className="sup-stats">
                        <div className="sup-stat">
                            <div>
                                <span className="sup-stat-val">{filteredSuppliers.length}</span>
                                <span className="sup-stat-lbl">
                                    {recordStatus === "inactive"
                                        ? "Inactive Suppliers"
                                        : filtersActive ? "Filtered Suppliers" : "Total Suppliers"}
                                </span>
                            </div>
                        </div>
                        <div className="sup-stat">
                            <div>
                                <span className="sup-stat-val">{ownFleetCount}</span>
                                <span className="sup-stat-lbl">Own Fleet</span>
                            </div>
                        </div>
                        <div className="sup-stat">
                            <div>
                                <span className="sup-stat-val">{dcoCount}</span>
                                <span className="sup-stat-lbl">DCO</span>
                            </div>
                        </div>
                        <div className="sup-stat">
                            <div>
                                <span className="sup-stat-val">{vdpCount}</span>
                                <span className="sup-stat-lbl">VDP</span>
                            </div>
                        </div>
                        <div className="sup-stat">
                            <div>
                                <span className="sup-stat-val">{activeCount}</span>
                                <span className="sup-stat-lbl">Active</span>
                            </div>
                        </div>
                        <div className="sup-stat">
                            <div>
                                <span className="sup-stat-val">{inactiveCount}</span>
                                <span className="sup-stat-lbl">Inactive</span>
                            </div>
                        </div>
                        <div className="sup-stat">
                            <div>
                                <span className="sup-stat-val">₹{totalDues.toLocaleString("en-IN")}</span>
                                <span className="sup-stat-lbl">{filtersActive ? "Filtered Dues" : "Total Dues"}</span>
                            </div>
                        </div>
                    </div>

                    <div className="sup-toolbar">
                        <div className="sup-toolbar-scroll">
                            <div className="sup-search">
                                <FiSearch size={14} />
                                <input
                                    type="text"
                                    placeholder="Search by name, ID, code, mobile, city…"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                {searchTerm && (
                                    <button className="sup-clear" onClick={() => setSearchTerm("")} aria-label="Clear">
                                        <FiX size={12} />
                                    </button>
                                )}
                            </div>

                            <div className="sup-filters">
                                <div className="sup-filter-dropdown" ref={filterPanelRef}>
                                    <button
                                        type="button"
                                        className="sup-filter-toggle-btn"
                                        onClick={() => setFilterPanelOpen((open) => !open)}
                                    >
                                        <FiFilter size={13} /> Filters
                                        {activeFilterCount > 0 && <span className="sup-badge">{activeFilterCount}</span>}
                                    </button>

                                    {filterPanelOpen && (
                                        <div className="sup-filter-panel">

                                            <div className="sup-filter-panel-field">
                                                <label>Status</label>
                                                <select value={recordStatus} onChange={(e) => setRecordStatus(e.target.value)}>
                                                    {RECORD_STATUS_OPTIONS.map((o) => (
                                                        <option key={o.value} value={o.value}>{o.label}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="sup-filter-panel-field">
                                                <label>Type</label>
                                                <select value={selectedSupplierType} onChange={(e) => setSelectedSupplierType(e.target.value)}>
                                                    <option value="">All Types</option>
                                                    {supplierTypes.map((t) => (
                                                        <option key={t.id} value={t.id}>{t.supplier_type_name}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="sup-filter-panel-field">
                                                <label>Business Status</label>
                                                <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                                                    <option value="">All Business Statuses</option>
                                                    {statuses.map((s) => (
                                                        <option key={s.id} value={s.id}>{s.name || s.status_name}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="sup-filter-panel-field">
                                                <label>Location</label>
                                                <select value={selectedLocation} onChange={(e) => setSelectedLocation(e.target.value)}>
                                                    <option value="">All Locations</option>
                                                    {locations.map((l) => (
                                                        <option key={l.id} value={l.id}>{l.location_name || l.name}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="sup-filter-panel-field">
                                                <label>Branch</label>
                                                <select value={selectedBranch} onChange={(e) => setSelectedBranch(e.target.value)}>
                                                    <option value="">All Branches</option>
                                                    {branchesForSelectedLocation.map((b) => (
                                                        <option key={b.id} value={b.id}>{b.branch_name}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="sup-filter-panel-field">
                                                <label>Outstanding</label>
                                                <select value={selectedOutstandingRange} onChange={(e) => setSelectedOutstandingRange(e.target.value)}>
                                                    {OUTSTANDING_RANGE_OPTIONS.map((o) => (
                                                        <option key={o.value} value={o.value}>{o.label}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <button
                                                type="button"
                                                className="sup-filter-panel-done"
                                                onClick={() => setFilterPanelOpen(false)}
                                            >
                                                Done
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <select value={selectedFinancialYear} onChange={(e) => handleFinancialYearChange(e.target.value)}>
                                    <option value="">Current Year</option>
                                    <option value="all">All Financial Years</option>
                                    {financialYears.map((fy) => (
                                        <option key={fy.id} value={fy.id}>{fy.fy_label}</option>
                                    ))}
                                </select>

                                {showFYDateRange && (
                                    <>
                                        <input
                                            type="date"
                                            value={dateFrom}
                                            min={selectedFYObj?.start_date}
                                            max={selectedFYObj?.end_date}
                                            onChange={(e) => setDateFrom(e.target.value)}
                                            className="sup-date-input"
                                            title={`From date (within ${selectedFYObj?.fy_label || "selected year"})`}
                                        />
                                        <input
                                            type="date"
                                            value={dateTo}
                                            min={selectedFYObj?.start_date}
                                            max={selectedFYObj?.end_date}
                                            onChange={(e) => setDateTo(e.target.value)}
                                            className="sup-date-input"
                                            title={`To date (within ${selectedFYObj?.fy_label || "selected year"})`}
                                        />
                                    </>
                                )}

                                {filtersActive && (
                                    <button className="sup-reset-btn" onClick={resetFilters}>
                                        Reset {activeFilterCount > 0 && <span className="sup-badge">{activeFilterCount}</span>}
                                    </button>
                                )}
                            </div>
                        </div>

                        {hasSupplierAccess && (
                            <button className="sup-add-btn" onClick={() => navigate("/add-supplier")}>
                                <FiPlus size={15} /> Add Supplier
                            </button>
                        )}
                    </div>

                    {errorMsg && (
                        <div className="sup-error">
                            <FiAlertCircle size={16} />
                            <span>{errorMsg}</span>
                            <button onClick={loadData}><FiRefreshCw size={13} /> Retry</button>
                        </div>
                    )}

                    <div className="sup-table-card">
                        <div className="sup-table-wrap">
                            <table className="sup-table">
                                <thead>
                                    <tr>
                                        <th>Photo</th>
                                        <th>Supplier Code</th>
                                        <th>Branch</th>
                                        <th>Type</th>
                                        <th>Name</th>
                                        <th>Mobile</th>
                                        <th>Status</th>
                                        <th>Outstanding</th>
                                        <th className="col-actions">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="9" className="sup-state">
                                                <div className="sup-loading">
                                                    <span className="sup-spinner" />
                                                    Loading suppliers…
                                                </div>
                                            </td>
                                        </tr>
                                    ) : paginatedSuppliers.length === 0 ? (
                                        <tr>
                                            <td colSpan="9" className="sup-state">
                                                <FiUsers size={28} style={{ opacity: 0.3 }} />
                                                <p>
                                                    {recordStatus === "inactive"
                                                        ? "No inactive (deleted) suppliers found."
                                                        : "No suppliers match your filters."}
                                                </p>
                                                <button className="sup-link-btn" onClick={resetFilters}>Clear filters</button>
                                            </td>
                                        </tr>
                                    ) : (
                                        paginatedSuppliers.map((s) => {
                                            const statusName = getStatusName(s.status_id);
                                            const hasDues = hasOutstandingField(s);
                                            const dues = Number(s.total_outstanding || 0);
                                            const isInactiveRecord = s.is_active === false;
                                            return (
                                                <tr
                                                    key={s.id}
                                                    className={`${canViewSuppliers ? "sup-row-clickable" : ""} ${deletingId === s.id ? "row-deleting" : ""} ${isInactiveRecord ? "row-inactive" : ""}`}
                                                    onClick={() => {
                                                        if (canViewSuppliers) {
                                                            openViewModal(s.id, s);
                                                        }
                                                    }}
                                                    title={canViewSuppliers ? "Click to view full details" : ""}
                                                >
                                                    <td>
                                                        {s.photo_url && isImageUrl(s.photo_url) ? (
                                                            <img
                                                                src={resolveDocUrl(s.photo_url)}
                                                                alt={s.supplier_name}
                                                                className="sup-avatar-thumb"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openPreview(resolveDocUrl(s.photo_url), `${s.supplier_name} — Photo`);
                                                                }}
                                                            />
                                                        ) : (
                                                            <div className="sup-avatar-fallback">
                                                                <FiUser size={16} />
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="cell-id">{s.supplier_code || "-"}</td>
                                                    <td>{getBranchName(s.branch_id)}</td>
                                                    <td>
                                                        <span className="sup-type-chip">
                                                            {getSupplierTypeName(s.supplier_type_id)}
                                                        </span>
                                                    </td>
                                                    <td className="cell-name">
                                                        {s.supplier_name}
                                                        {isInactiveRecord && (
                                                            <span className="sup-status status-inactive" style={{ marginLeft: 8 }}>
                                                                Deleted
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td>{s.mobile || "-"}</td>
                                                    <td>
                                                        <span className={`sup-status ${statusClass(statusName)}`}>
                                                            {statusName}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {hasDues ? (
                                                            <span className={dues > 0 ? "sup-dues has" : "sup-dues none"}>
                                                                ₹{dues.toLocaleString("en-IN")}
                                                            </span>
                                                        ) : "-"}
                                                    </td>
                                                    <td onClick={(e) => e.stopPropagation()}>
                                                        <div className="sup-actions-dropdown">
                                                            <button
                                                                type="button"
                                                                className={`sup-actions-btn ${openActionRowId === s.id ? "sup-actions-btn--active" : ""}`}
                                                                title="Actions"
                                                                ref={(el) => { actionButtonRefs.current[s.id] = el; }}
                                                                onMouseDown={(e) => e.stopPropagation()}
                                                                onClick={(e) => toggleActionMenu(e, s.id, isInactiveRecord)}
                                                            >
                                                                Actions
                                                                <FiChevronDown size={13} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {!loading && filteredSuppliers.length > 0 && (
                            <div className="sup-pagination">
                                <span className="sup-pagination-info">
                                    Showing {rangeStart}–{rangeEnd} of {filteredSuppliers.length} suppliers
                                </span>
                                <div className="sup-pagination-controls">
                                    <button className="sup-page-btn" onClick={() => goToPage(currentPage - 1)}
                                        disabled={currentPage === 1} aria-label="Previous page">
                                        <FiChevronLeft size={14} />
                                    </button>
                                    <span className="sup-page-current">Page {currentPage} of {totalPages}</span>
                                    <button className="sup-page-btn" onClick={() => goToPage(currentPage + 1)}
                                        disabled={currentPage === totalPages} aria-label="Next page">
                                        <FiChevronRight size={14} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                </div>
            </div>

            {openActionRowId !== null && menuPos && createPortal(
                <div
                    className="sup-actions-menu sup-actions-menu-portal"
                    ref={actionMenuRef}
                    style={{
                        top: menuPos.top,
                        left: menuPos.left,
                        transform: "translateX(-100%)",
                    }}
                >
                    <>
                        {canViewSuppliers && (
                            <button
                                className="sup-actions-menu-item"
                                onClick={() => {
                                    const id = openActionRowId;
                                    closeActionMenu();
                                    openViewModal(id, openActionSupplier);
                                }}
                            >
                                <FiEye size={13} /> View
                            </button>
                        )}

                        {canEditSuppliers && (
                            <button
                                className="sup-actions-menu-item"
                                onClick={() => {
                                    const id = openActionRowId;
                                    closeActionMenu();
                                    navigate(`/edit-supplier/${id}`);
                                }}
                            >
                                <FiEdit2 size={13} /> Edit
                            </button>
                        )}

                        {canDeleteSuppliers && !openActionIsInactive && (
                            <button
                                className="sup-actions-menu-item delete"
                                onClick={() => {
                                    const id = openActionRowId;
                                    closeActionMenu();
                                    handleDelete(id);
                                }}
                                disabled={deletingId === openActionRowId}
                            >
                                <FiTrash2 size={13} /> Delete
                            </button>
                        )}

                        {!canViewSuppliers && !canEditSuppliers && !canDeleteSuppliers && (
                            <div style={{ padding: "8px 12px", fontSize: 12, color: "#64748b" }}>
                                View only
                            </div>
                        )}
                    </>
                </div>,
                document.body
            )}

            {docModalSupplier && (
                <div className="sup-docmodal-overlay" onClick={closeDocModal}>
                    <div className="sup-docmodal" onClick={(e) => e.stopPropagation()}>
                        <div className="sup-docmodal-header">
                            <div>
                                <h3>Documents</h3>
                                <p>
                                    — {docModalSupplier.supplier_name}
                                    {docModalSupplier.mobile && <> &nbsp;{docModalSupplier.mobile}</>}
                                </p>
                            </div>
                            <button className="sup-preview-close" onClick={closeDocModal} aria-label="Close">
                                <FiX size={18} />
                            </button>
                        </div>

                        <div className="sup-docmodal-grid">
                            {DOC_FIELDS.map(({ key, label, icon: Icon }) => {
                                const rawUrl = docModalSupplier[key];
                                const url = resolveDocUrl(rawUrl);
                                return (
                                    <div className="sup-doc-card" key={key}>
                                        <span className="sup-doc-card-label">{label}</span>
                                        {url ? (
                                            isImageUrl(url) ? (
                                                <img
                                                    src={url}
                                                    alt={label}
                                                    className="sup-doc-card-img"
                                                    onClick={() => openPreview(url, `${docModalSupplier.supplier_name} — ${label}`)}
                                                />
                                            ) : (
                                                <div
                                                    className="sup-doc-card-file"
                                                    onClick={() => openPreview(url, `${docModalSupplier.supplier_name} — ${label}`)}
                                                >
                                                    <Icon size={28} />
                                                </div>
                                            )
                                        ) : (
                                            <div className="sup-doc-card-empty">
                                                <Icon size={24} />
                                                <span>Not uploaded</span>
                                            </div>
                                        )}
                                        {url && (
                                            <a
                                                href={url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="sup-doc-card-view"
                                            >
                                                <FiEye size={13} /> View full
                                            </a>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="sup-docmodal-footer">
                            {docCount(docModalSupplier)} of {DOC_FIELDS.length} documents uploaded
                        </div>
                    </div>
                </div>
            )}
            {previewUrl && (
                <div className="sup-preview-overlay" onClick={closePreview}>
                    <div className="sup-preview-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="sup-preview-header">
                            <span>{previewLabel}</span>
                            <button className="sup-preview-close" onClick={closePreview} aria-label="Close">
                                <FiX size={16} />
                            </button>
                        </div>
                        <div className="sup-preview-body">
                            {isImageUrl(previewUrl) ? (
                                <img src={previewUrl} alt={previewLabel} className="sup-preview-image" />
                            ) : (
                                <iframe src={previewUrl} title={previewLabel} className="sup-preview-pdf" />
                            )}
                        </div>
                        <div className="sup-preview-footer">
                            <a href={previewUrl} target="_blank" rel="noreferrer" className="sup-preview-link">
                                <FiEye size={13} /> Open in new tab
                            </a>
                            <a href={previewUrl} download className="sup-preview-link">
                                Download
                            </a>
                        </div>
                    </div>
                </div>
            )}
            {viewSupplier && (
                <div className="sup-view-overlay" onClick={closeViewModal}>
                    <div className="sup-view-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="sup-view-header">
                            <div className="sup-view-header-left">
                                {viewSupplier.photo_url && isImageUrl(viewSupplier.photo_url) ? (
                                    <img src={resolveDocUrl(viewSupplier.photo_url)} alt={viewSupplier.supplier_name} className="sup-view-avatar" />
                                ) : (
                                    <div className="sup-view-avatar sup-view-avatar-fallback">
                                        <FiUser size={22} />
                                    </div>
                                )}
                                <div>
                                    <h3>
                                        {viewSupplier.supplier_name || "Supplier"}
                                        {viewSupplier.is_active === false && (
                                            <span className="sup-status status-inactive" style={{ marginLeft: 8 }}>
                                                Deleted
                                            </span>
                                        )}
                                    </h3>
                                    <p>{viewSupplier.supplier_code || "-"} {viewSupplier.mobile && <>&nbsp;·&nbsp;{viewSupplier.mobile}</>}</p>
                                </div>
                            </div>
                            <div className="sup-view-header-actions">
                                <button className="sup-view-print-btn" onClick={handlePrintView} disabled={printing}>
                                    <FiPrinter size={14} /> {printing ? "Preparing…" : "Print"}
                                </button>
                                <button className="sup-view-download-btn" onClick={handleDownloadView} disabled={pdfGenerating}>
                                    <FiDownload size={14} /> {pdfGenerating ? "Preparing…" : "Download"}
                                </button>
                                <button className="sup-preview-close" onClick={closeViewModal} aria-label="Close">
                                    <FiX size={18} />
                                </button>
                            </div>
                        </div>

                        <div className="sup-view-body">
                            {viewLoading && (
                                <div className="sup-view-loading">
                                    <span className="sup-spinner" />
                                    Loading full details…
                                </div>
                            )}

                            {viewError && (
                                <div className="sup-error" style={{ marginBottom: 16 }}>
                                    <FiAlertCircle size={16} />
                                    <span>{viewError}</span>
                                </div>
                            )}

                            <div className="sup-view-section">
                                <h4>Contact</h4>
                                <div className="sup-view-grid">
                                    <div className="sup-view-field"><span>Supplier Code</span><strong>{viewSupplier.supplier_code || "-"}</strong></div>
                                    <div className="sup-view-field"><span>Name</span><strong>{viewSupplier.supplier_name || "-"}</strong></div>
                                    <div className="sup-view-field"><span>Mobile</span><strong>{viewSupplier.mobile || "-"}</strong></div>
                                    <div className="sup-view-field"><span>Alt. Mobile</span><strong>{viewSupplier.alternate_mobile || "-"}</strong></div>
                                    <div className="sup-view-field"><span>Email</span><strong>{viewSupplier.email || "-"}</strong></div>
                                    <div className="sup-view-field"><span>Aadhaar</span><strong>{viewSupplier.aadhaar_number || "-"}</strong></div>
                                </div>
                            </div>

                            <div className="sup-view-section">
                                <h4>Location</h4>
                                <div className="sup-view-grid">
                                    <div className="sup-view-field"><span>City</span><strong>{viewSupplier.city || "-"}</strong></div>
                                    <div className="sup-view-field"><span>State</span><strong>{viewSupplier.state || "-"}</strong></div>
                                    <div className="sup-view-field"><span>Pincode</span><strong>{viewSupplier.pincode || "-"}</strong></div>
                                    <div className="sup-view-field"><span>Branch</span><strong>{getBranchName(viewSupplier.branch_id)}</strong></div>
                                    <div className="sup-view-field"><span>Service Location</span><strong>{getLocationName(viewSupplier.service_location_id)}</strong></div>
                                </div>
                            </div>

                            <div className="sup-view-section">
                                <h4>Status</h4>
                                <div className="sup-view-grid">
                                    <div className="sup-view-field"><span>Type</span><strong>{getSupplierTypeName(viewSupplier.supplier_type_id)}</strong></div>
                                    <div className="sup-view-field"><span>Status</span><strong>{getStatusName(viewSupplier.status_id)}</strong></div>
                                    <div className="sup-view-field"><span>Financial Year</span><strong>{getFinancialYearLabel(viewSupplier.financial_year_id)}</strong></div>
                                    <div className="sup-view-field">
                                        <span>Outstanding</span>
                                        <strong>
                                            {hasOutstandingField(viewSupplier)
                                                ? `₹${Number(viewSupplier.total_outstanding || 0).toLocaleString("en-IN")}`
                                                : "-"}
                                        </strong>
                                    </div>
                                    <div className="sup-view-field">
                                        <span>Max Vehicle Capacity</span>
                                        <strong>
                                            {hasOutstandingField(viewSupplier)
                                                ? (viewSupplier.max_vehicle_capacity ?? "-")
                                                : "-"}
                                        </strong>
                                    </div>
                                    <div className="sup-view-field">
                                        <span>Record Status</span>
                                        <strong>{viewSupplier.is_active === false ? "Inactive (Deleted)" : "Active"}</strong>
                                    </div>
                                    <div className="sup-view-field sup-view-field-wide"><span>Remarks</span><strong>{viewSupplier.remarks || "-"}</strong></div>
                                </div>
                            </div>
                            <div className="sup-view-section">
                                <h4>Audit</h4>
                                <div className="sup-view-grid">
                                    <div className="sup-view-field">
                                        <span>Created By</span>
                                        <strong>{viewSupplier.created_by_name || viewSupplier.created_by || "-"}</strong>
                                    </div>
                                    <div className="sup-view-field">
                                        <span>Updated By</span>
                                        <strong>{viewSupplier.updated_by_name || viewSupplier.updated_by || "-"}</strong>
                                    </div>
                                    <div className="sup-view-field">
                                        <span>Created On</span>
                                        <strong>{formatServerDateTime(viewSupplier.created_at)}</strong>
                                    </div>
                                    <div className="sup-view-field">
                                        <span>Updated On</span>
                                        <strong>{formatServerDateTime(viewSupplier.updated_at)}</strong>
                                    </div>
                                </div>
                            </div>
                            <div className="sup-view-section">
                                <h4>Documents</h4>
                                <div className="sup-view-doc-list">
                                    {DOC_FIELDS.map(({ key, label, icon: Icon }) => {
                                        const rawUrl = viewSupplier[key];
                                        const url = resolveDocUrl(rawUrl);
                                        const showThumb = url && isImageUrl(url);
                                        return (
                                            <div className="sup-view-doc-row" key={key}>
                                                {showThumb ? (
                                                    <img
                                                        src={url}
                                                        alt={label}
                                                        className="sup-view-doc-thumb"
                                                        onClick={() => openPreview(url, `${viewSupplier.supplier_name} — ${label}`)}
                                                    />
                                                ) : (
                                                    <div className="sup-view-doc-thumb sup-view-doc-thumb-icon">
                                                        <Icon size={14} />
                                                    </div>
                                                )}
                                                <span>{label}</span>
                                                {url ? (
                                                    <a
                                                        href={url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        onClick={(e) => {
                                                            if (showThumb) {
                                                                e.stopPropagation();
                                                            }
                                                        }}
                                                    >
                                                        View / Download
                                                    </a>
                                                ) : (
                                                    <em>Not uploaded</em>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {(() => {
                                const extraEntries = Object.entries(viewSupplier).filter(
                                    ([k, v]) => !VIEW_KNOWN_KEYS.has(k) && v !== null && v !== undefined && v !== ""
                                );
                                if (extraEntries.length === 0) return null;
                                return (
                                    <div className="sup-view-section">
                                        <h4>Additional Details</h4>
                                        <div className="sup-view-grid">
                                            {extraEntries.map(([k, v]) => (
                                                <div className="sup-view-field" key={k}>
                                                    <span>{humanizeKey(k)}</span>
                                                    <strong>{typeof v === "object" ? JSON.stringify(v) : String(v)}</strong>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}